import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getGuardianAlphaService,
  restartGuardianAlpha,
} from '@/api/guardianAlpha'
import { useAuth } from '@/auth/useAuth'
import type { GuardianAlphaService, NodeDetail } from '@/types'
import { parseCapabilities } from './utils'

export function useGuardianAlphaRestart(node: NodeDetail) {
  const { hasPermission } = useAuth()
  const [service, setService] = useState<GuardianAlphaService | null>(null)
  const [missing, setMissing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [restarting, setRestarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const capabilities = useMemo(
    () => parseCapabilities(node.capabilities),
    [node.capabilities],
  )
  const canRestart = hasPermission('guardian-alpha-restart:execute')
  const supportsRestart = capabilities.has('guardian-alpha.restart.v1')

  const loadService = useCallback(async () => {
    try {
      const loaded = await getGuardianAlphaService(node.id)
      setService(loaded)
      setMissing(false)
      setError(null)
    } catch (caught: unknown) {
      const apiError = caught as { status?: number; message?: string }
      if (apiError.status === 404) {
        setService(null)
        setMissing(true)
        setError(null)
      } else {
        setError(apiError.message ?? 'GuardianAlpha 상태를 불러오지 못했습니다.')
      }
    } finally {
      setLoading(false)
    }
  }, [node.id])

  useEffect(() => {
    setService(null)
    setMissing(false)
    setLoading(true)
    setError(null)
    void loadService()
  }, [loadService])

  useEffect(() => {
    if (!service || service.last_restart_status !== 'running') return
    const timer = window.setInterval(loadService, 3000)
    return () => window.clearInterval(timer)
  }, [loadService, service])

  const restartDisabledReason = useMemo(() => {
    if (loading) return 'GuardianAlpha 상태를 불러오는 중입니다.'
    if (!service) return 'GuardianAlpha service가 등록되지 않았습니다.'
    if (!canRestart) return 'GuardianAlpha Restart 실행 권한이 필요합니다.'
    if (node.status === 'offline') return 'Node가 offline입니다.'
    if (!supportsRestart) return 'Agent가 GuardianAlpha Restart를 지원하지 않습니다.'
    if (!service.restart_enabled) return '최신 start.sh 확인 후 admin이 Restart를 활성화해야 합니다.'
    if (service.guardian_nodes.length === 0) return '등록된 GuardianNode가 없습니다.'
    if (service.last_restart_status === 'running' || restarting) return 'Restart가 실행 중입니다.'
    return null
  }, [canRestart, loading, node.status, restarting, service, supportsRestart])

  const restart = useCallback(async () => {
    if (!service || restartDisabledReason) return
    const targets = service.guardian_nodes
      .map((item) => `- ${item.display_name} (${item.process_entrypoint})`)
      .join('\n')
    const confirmed = window.confirm(
      `물리 노드의 상위 /home/node/start.sh를 실행합니다.\n\n`
      + `등록된 GuardianNode ${service.guardian_nodes.length}개:\n${targets}\n\n`
      + 'IO 보조 프로세스와 실제 추론 결과는 확인하지 않습니다. 계속할까요?',
    )
    if (!confirmed) return
    setRestarting(true)
    setError(null)
    try {
      await restartGuardianAlpha(
        node.id,
        'Node Detail GuardianAlpha restart',
      )
      setService((current) => current
        ? { ...current, last_restart_status: 'running', last_restart_error: null }
        : current)
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? 'Restart 명령 생성에 실패했습니다.')
    } finally {
      setRestarting(false)
    }
  }, [node.id, restartDisabledReason, service])

  return {
    service,
    missing,
    loading,
    restarting,
    error,
    restartDisabledReason,
    restart,
  }
}
